import { Analytics } from '@util/Analytics';
import React, { ReactElement } from 'react';
import { LayoutScreenConfig, ScreenType } from '../../../config/FlowTypes';
import { UserDataState } from '../../../store/userData/UserDataState';
import { useAppStore } from '../../../store/Store';
import { dispatchUserData } from '../../../store/userData/UserDataActions';
import { NavigationProps } from '../FlowNavigation';
import {
  CoverageEmployerLayout,
  CoverageEmployerLayoutConfig,
  CoverageEmployerSubmission,
} from '../layouts/CoverageEmployerLayout';

export type CoverageEmployerScreenConfig<ScreenNames extends string> = LayoutScreenConfig<
  ScreenType.CoverageEmployer,
  CoverageEmployerLayoutConfig,
  ScreenNames
>;

type Props<ScreenNames extends string> = NavigationProps<
  CoverageEmployerScreenConfig<ScreenNames>,
  ScreenNames
>;

const CONSENT_YES = 'Yes';
const CONSENT_NO = 'No';
/** Stored beside the screen's own answer; not a screen, so not in CoverageScreenName. */
const OUTREACH_CONSENT_KEY: keyof UserDataState = 'coverageEmployerOutreachConsent';

/**
 * "Who is your employer?" with the optional employer-outreach consent. Stores the company name under
 * the screen name and the consent under its own key so either can be read independently later; the
 * consent has no side effect yet (outreach is wired up with the results work).
 */
export const CoverageEmployerScreen = <ScreenNames extends string>(
  props: Props<ScreenNames>
): ReactElement => {
  const prefill = useAppStore((state) => ({
    employer: state.userData.coverageUsEmployer?.[0],
    outreachConsent: state.userData.coverageEmployerOutreachConsent?.[0] === CONSENT_YES,
  }));

  function onSubmit(submission: CoverageEmployerSubmission): void {
    const consent = submission.outreachConsent ? CONSENT_YES : CONSENT_NO;
    dispatchUserData({
      [props.screenName]: [submission.employer],
      [OUTREACH_CONSENT_KEY]: [consent],
    });

    // The typed employer name is sent so Customer.io campaigns can use it; work email and address stay out.
    Analytics.track({
      event: Analytics.Event.o_question_answered,
      question: props.screenName,
      answer: submission.employer,
    });
    Analytics.track({
      event: Analytics.Event.o_question_answered,
      question: OUTREACH_CONSENT_KEY,
      answer: consent,
    });

    props.navigation.next(props);
  }

  return (
    <CoverageEmployerLayout {...props.layout} prefill={prefill} onPressPrimaryButton={onSubmit} />
  );
};
